import { Component, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../../../shared/material/material.module';
import { CommonModule } from '@angular/common';
import { 
  AccessPlan, 
  ProductPackage,
  AccessPlanControllerService,
  PlanUpgradeRequestDTO,
  PlanUpgradeResponseDTO
} from '../../../../core/generated';
import { MatSnackBar } from '@angular/material/snack-bar';
import { modalFadeIn } from '../../shared/animations';

/**
 * PlanUpgradeConfirmationComponent
 * Modal de confirmação do upgrade com comparação entre planos
 */
@Component({
  selector: 'app-plan-upgrade-confirmation',
  standalone: true,
  imports: [MaterialModule, CommonModule],
  templateUrl: './plan-upgrade-confirmation.component.html',
  styleUrl: './plan-upgrade-confirmation.component.scss',
  animations: [modalFadeIn]
})
export class PlanUpgradeConfirmationComponent {
  dialogRef = inject(MatDialogRef<PlanUpgradeConfirmationComponent>);
  data = inject<{ currentPlan: AccessPlan; targetPackage: ProductPackage }>(MAT_DIALOG_DATA);

  private accessPlanService = inject(AccessPlanControllerService);
  private snackBar = inject(MatSnackBar);

  isProcessing = signal(false);
  upgradeSuccess = signal(false);
  upgradeResponse = signal<PlanUpgradeResponseDTO | null>(null);

  /**
   * Calcula o valor total de um pacote
   */
  calculatePackageTotal(pkg: ProductPackage): number {
    if (!pkg.items) return 0;

    const itemsTotal = pkg.items.reduce((sum, item) => {
      return sum + ((item.quantity || 0) * (item.unitPrice || 0));
    }, 0);

    const discountsTotal = (pkg.discounts || []).reduce((sum, discount) => {
      if (typeof discount === 'object' && 'discountValue' in discount) {
        return sum + (typeof discount.discountValue === 'number' ? discount.discountValue : 0);
      }
      return sum;
    }, 0);

    // Removed reference to non-existent property 'percentageDiscounts'
    const percentageDiscountAmount = 0; // Assuming no percentage discounts are applied

    return itemsTotal - discountsTotal - percentageDiscountAmount;
  }

  /**
   * Calcula a diferença de valor entre os planos
   */
  getPriceDifference(): number {
    const currentValue = this.data.currentPlan.finalTotal || 0;
    const targetValue = this.calculatePackageTotal(this.data.targetPackage);
    return targetValue - currentValue;
  }

  /**
   * Identifica itens adicionados no novo plano
   */
  getAddedItems(): string[] {
    const currentItems = this.data.currentPlan.items || [];
    const targetItems = this.data.targetPackage.items || [];
    
    const currentItemNames = new Set(currentItems.map(item => item.product?.name));
    
    return targetItems
      .filter(item => !currentItemNames.has(item.product?.name))
      .map(item => `${item.product?.name || 'Item'} (${item.quantity}x)`);
  }

  /**
   * Identifica itens com quantidade aumentada
   */
  getUpgradedItems(): string[] {
    const currentItems = this.data.currentPlan.items || [];
    const targetItems = this.data.targetPackage.items || [];
    
    const upgraded: string[] = [];
    
    targetItems.forEach(targetItem => {
      const currentItem = currentItems.find(ci => ci.product?.name === targetItem.product?.name);
      if (currentItem && (targetItem.quantity || 0) > (currentItem.quantity || 0)) {
        upgraded.push(
          `${targetItem.product?.name}: ${currentItem.quantity} → ${targetItem.quantity}`
        );
      }
    });
    
    return upgraded;
  }

  /**
   * Processa o upgrade
   */
  processUpgrade(): void {
    const planId = this.data.currentPlan.id;
    const targetPackageId = this.data.targetPackage.id;
    
    if (!planId || !targetPackageId) {
      this.snackBar.open('Erro: dados inválidos para upgrade', 'Fechar', { duration: 4000 });
      return;
    }

    this.isProcessing.set(true);

    const request: PlanUpgradeRequestDTO = {
      targetPlanId: targetPackageId
    };

    this.accessPlanService.upgradePlan(planId, request).subscribe({
      next: (response) => {
        this.upgradeResponse.set(response);
        this.upgradeSuccess.set(true);
        this.isProcessing.set(false);
        
        this.snackBar.open('Upgrade realizado com sucesso!', 'Fechar', {
          duration: 5000,
          panelClass: ['success-snackbar']
        });
      },
      error: (error) => {
        console.error('Erro ao processar upgrade:', error);
        
        let errorMessage = 'Erro ao processar upgrade. Tente novamente.';
        if (error.error?.message) {
          errorMessage = error.error.message;
        }
        
        this.snackBar.open(errorMessage, 'Fechar', {
          duration: 6000,
          panelClass: ['error-snackbar']
        });
        
        this.isProcessing.set(false);
      }
    });
  }

  /**
   * Formata valor em moeda brasileira
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Formata data
   */
  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch {
      return 'N/A';
    }
  }

  /**
   * Fecha o modal
   */
  close(): void {
    this.dialogRef.close(this.upgradeSuccess() ? { upgraded: true } : null);
  }
}
